import React from 'react';
import { Info, MessageSquare } from 'lucide-react';
import { useQuery } from 'react-query';
import { timelineApi } from '../../services/api';
import { useAskSidebar } from '../../contexts/AskSidebarContext';

interface DerivedOutputBannerProps {
  caseId: string;
  /** Human-readable label for this analysis, e.g. "causation analysis" */
  analysisLabel: string;
  /** The question sent to the AI assistant when the user clicks "Evaluate impact" */
  evaluatePrompt: string;
}

/**
 * Subtle banner shown on derived-output tabs when chronology events have been
 * user-corrected. Warns that this analysis was generated from the original
 * (unedited) data and offers a link to ask the AI assistant about the impact.
 */
export const DerivedOutputBanner: React.FC<DerivedOutputBannerProps> = ({
  caseId,
  analysisLabel,
  evaluatePrompt,
}) => {
  const { submitAskMessage } = useAskSidebar();

  const { data: timelineData } = useQuery(
    ['timeline', caseId, {}],
    () => timelineApi.getTimeline(caseId, {}),
    { enabled: !!caseId, staleTime: 60_000 }
  );

  const events = timelineData?.timeline?.events || [];
  const editedCount = events.filter((e: any) => e.isEdited).length;

  if (editedCount === 0) return null;

  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-cyan-200 dark:border-cyan-800/60 bg-cyan-50/60 dark:bg-cyan-900/15 px-3.5 py-2.5">
      <Info className="w-4 h-4 text-cyan-500 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-cyan-800 dark:text-cyan-300 leading-relaxed">
        {editedCount === 1 ? '1 chronology event has' : `${editedCount} chronology events have`} been
        corrected since this {analysisLabel} was generated. Edits to the chronology are not
        automatically reflected here and may introduce inconsistencies.{' '}
        <button
          onClick={() => submitAskMessage(evaluatePrompt)}
          className="inline-flex items-center gap-1 font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 underline decoration-cyan-400/40 underline-offset-2 transition-colors"
        >
          <MessageSquare className="w-3 h-3" />
          Evaluate impact
        </button>
      </p>
    </div>
  );
};
